import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Edit, Trash2 } from 'lucide-react';
import { 
  userService,
  departmentService,
} from '../services/firestoreService';
import rbacService from '../services/rbacService';
import authService from '../services/authService';
import auditService from '../services/auditService';
import { useNotifications } from './NotificationSystem';
import Modal from './ui/Modal';
import './UserManagement.css';

const UserManagement = ({ departments: propDepartments = [] }) => {
  const { t } = useTranslation(['userManagement', 'common']);
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [showUserForm, setShowUserForm] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const { notify } = useNotifications();

  // Form states
  const [userForm, setUserForm] = useState({
    badge_id: '',
    first_name: '',
    last_name: '',
    employee_id: '',
    phone_primary: '',
    email: '',
    password: '',
    confirmPassword: '',
    role_id: '',
    department_id: '',
    primary_department_id: '',
    secondary_department_ids: [],
    specialty: '',
    status: 'active'
  });

  const generateId = () => Date.now().toString() + Math.random().toString(36).substr(2, 9);

  // Helper functions to get human-readable names
  const getRoleName = (roleId) => {
    if (!roleId) return t('noRole');
    const role = roles.find(r => r.id === roleId);
    return role?.name || roleId;
  };

  const getDepartmentName = (departmentId) => {
    if (!departmentId) return t('noDepartment');
    const department = departments.find(d => d.id === departmentId);
    return department?.name || departmentId;
  };

  const loadData = useCallback(async () => {
    const startTime = performance.now();
    
    try {
      setLoading(true);
      setError(null);
      
      console.log('⏱️ Starting User Management data load...');
      
      // Load RBAC roles (assignable only — superadmin is platform-exclusive)
      const rolesStart = performance.now();
      await rbacService.initialize();
      const rbacRoles = rbacService.getAssignableRoles();
      const rolesData = Object.entries(rbacRoles).map(([key, role]) => ({
        id: key,
        name: role.name,
        description: role.description,
        permissions: role.permissions
      }));
      console.log(`⏱️ Roles loaded in ${(performance.now() - rolesStart).toFixed(2)}ms`);
      
      // Set roles immediately (no need to wait)
      setRoles(rolesData);
      
      // Load users and departments in parallel (faster)
      const dataStart = performance.now();
      const [usersData, departmentsData] = await Promise.all([
        userService.getAll(),
        propDepartments.length > 0 ? Promise.resolve(propDepartments) : departmentService.getAll()
      ]);
      console.log(`⏱️ Users and departments loaded in ${(performance.now() - dataStart).toFixed(2)}ms`);
      
      setUsers(usersData || []);
      setDepartments(departmentsData || []);
      
      const totalTime = (performance.now() - startTime).toFixed(2);
      console.log(`✅ User Management loaded ${usersData?.length || 0} users and ${departmentsData?.length || 0} departments in ${totalTime}ms`);
      
    } catch (err) {
      console.error('❌ Error loading user data:', err);
      setError(t('failedToLoadUsers'));
    } finally {
      setLoading(false);
    }
  }, [propDepartments, t]);

  useEffect(() => {
    loadData();
  }, [propDepartments]);

  // CRUD Operations
  const addUser = async () => {
    try {
      // Validate required fields
      if (!userForm.email || !userForm.password || !userForm.first_name || !userForm.last_name) {
        setError(t('requiredFields'));
        return;
      }
      if (userForm.password !== userForm.confirmPassword) {
        setError(t('passwordsDoNotMatch'));
        return;
      }
      if (userForm.password.length < 6) {
        setError(t('passwordMinLength'));
        return;
      }

      setLoading(true);
      setError(null);

      // Create user via authService (which calls Cloud Function)
      const userData = {
        badge_id: userForm.badge_id || `BADGE-${Date.now()}`,
        first_name: userForm.first_name,
        last_name: userForm.last_name,
        employee_id: userForm.employee_id || `EMP-${Date.now()}`,
        phone_primary: userForm.phone_primary || '',
        email: userForm.email,
        password: userForm.password,
        role_id: userForm.role_id || 'execution',
        primary_department_id: userForm.primary_department_id || '',
        secondary_department_ids: userForm.secondary_department_ids || [],
        specialty: userForm.specialty || '',
        status: userForm.status || 'active',
        initials: `${userForm.first_name.charAt(0)}${userForm.last_name.charAt(0)}`.toUpperCase(),
        full_name: `${userForm.first_name} ${userForm.last_name}`
      };

      console.log('Creating user with data:', { ...userData, password: '[REDACTED]' });

      const result = await authService.createUser(userData);
      
      console.log('User created successfully:', result);

      // Reload users list
      await loadData();

      setShowUserForm(false);
      
      // Audit log
      auditService.logCreate('users', result.uid || result.userId, userData.full_name, {
        email: userData.email,
        role: userData.role_id
      });

      // Reset form
      setUserForm({
        badge_id: '',
        first_name: '',
        last_name: '',
        employee_id: '',
        phone_primary: '',
        email: '',
        password: '',
        confirmPassword: '',
        role_id: '',
        department_id: '',
        primary_department_id: '',
        secondary_department_ids: [],
        specialty: '',
        status: 'active'
      });

      setError(null);
      
      // Show success notification instead of browser alert
      notify.userCreated(userData.full_name, userData.email);

    } catch (err) {
      console.error('Error creating user:', err);
      setError(err.message || t('failedToCreateUser'));
      
      // Show error notification
      notify.error(err.message || t('failedToCreateUser'), t('userCreationFailed'));
    } finally {
      setLoading(false);
    }
  };

  const startEditingUser = (user) => {
    setSelectedUser(user);
    setEditingUser({ ...user }); // Create a copy for editing
    // Populate userForm with user data (form uses userForm for most fields in edit mode)
    setUserForm(prev => ({
      ...prev,
      badge_id: user.badge_id || '',
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      employee_id: user.employee_id || '',
      phone_primary: user.phone_primary || '',
      email: user.email || '',
      password: '',
      confirmPassword: '',
      role_id: user.role_id || '',
      primary_department_id: user.primary_department_id || '',
      secondary_department_ids: user.secondary_department_ids || [],
      specialty: user.specialty || '',
      status: user.status || 'active'
    }));
    setShowUserForm(true); // Show the modal
    setSaveSuccess(false); // Reset success state
  };

  const cancelEditing = () => {
    setSelectedUser(null);
    setEditingUser(null);
    setShowUserForm(false); // Hide the modal
    setSaveSuccess(false); // Reset success state
  };

  const saveUserChanges = async () => {
    if (!editingUser || !selectedUser) return;
    
    try {
      setLoading(true);
      
      // Merge editingUser with userForm (form uses both - badge/employee from editingUser, rest from userForm)
      const merged = {
        ...editingUser,
        first_name: userForm.first_name,
        last_name: userForm.last_name,
        email: userForm.email,
        phone_primary: userForm.phone_primary,
        role_id: userForm.role_id,
        primary_department_id: userForm.primary_department_id || null,
        secondary_department_ids: userForm.secondary_department_ids || [],
        specialty: userForm.specialty,
        status: userForm.status,
        full_name: `${userForm.first_name || ''} ${userForm.last_name || ''}`.trim() || selectedUser.full_name
      };
      
      // Get the changes by comparing merged with original
      const changes = {};
      Object.keys(merged).forEach(key => {
        const oldVal = selectedUser[key];
        const newVal = merged[key];
        if (key === 'password' || key === 'confirmPassword') return; // Don't send empty password
        if (newVal !== oldVal) {
          changes[key] = newVal;
        }
      });
      
      if (Object.keys(changes).length === 0) {
        notify.info(t('noChangesToSave'), t('noChanges'));
        return;
      }
      
      // Update in Firebase (including Firebase Auth if email changes)
      await authService.updateUser(selectedUser.id, {
        ...changes,
        updated_at: new Date().toISOString()
      });
      
      // Update local state
      setUsers(prev => prev.map(user => 
        user.id === selectedUser.id ? { ...user, ...changes, updatedAt: new Date().toISOString() } : user
      ));
      
      // Update selected user
      setSelectedUser(prev => ({ ...prev, ...changes, updatedAt: new Date().toISOString() }));
      
      // Audit log
      auditService.logUpdate('users', selectedUser.id, selectedUser.full_name || 'Unknown User', {
        action: 'Saved user changes',
        changes: changes
      });
      
      // Show success notification
      notify.success(t('userUpdatedSuccess'), t('userUpdate'));
      
      // Set success state for button feedback
      setSaveSuccess(true);
      
      // Reset success state after 2 seconds
      setTimeout(() => {
        setSaveSuccess(false);
      }, 2000);
      
      console.log('✅ User updated successfully:', selectedUser.id, changes);
      
    } catch (error) {
      console.error('❌ Error updating user:', error);
      notify.error(error.message || t('failedToUpdateUser'), t('updateFailed'));
    } finally {
      setLoading(false);
    }
  };

  const updateUser = async (id, updates) => {
    try {
      setLoading(true);
      
      // Update in Firebase
      await userService.update(id, {
        ...updates,
        updated_at: new Date().toISOString()
      });
      
      // Update local state
      setUsers(prev => prev.map(user => 
        user.id === id ? { ...user, ...updates, updatedAt: new Date().toISOString() } : user
      ));
      
      // Update selected user if it's the one being edited
      if (selectedUser && selectedUser.id === id) {
        setSelectedUser(prev => ({ ...prev, ...updates, updatedAt: new Date().toISOString() }));
      }
      
      // Show success notification
      notify.success(t('userUpdatedSuccess'), t('userUpdate'));
      
      console.log('✅ User updated successfully:', id, updates);
      
    } catch (error) {
      console.error('❌ Error updating user:', error);
      notify.error(error.message || t('failedToUpdateUser'), t('updateFailed'));
    } finally {
      setLoading(false);
    }
  };

  const deleteUser = (id) => {
    // Get user info for audit log before deleting
    const user = users.find(u => u.id === id);
    const userName = user?.full_name || t('unknownUser');
    
    setUsers(prev => prev.filter(user => user.id !== id));
    
    // Audit log
    auditService.logDelete('users', id, userName);
    
    // Clear selected user if it's the one being deleted
    if (selectedUser && selectedUser.id === id) {
      setSelectedUser(null);
    }
  };

  // Filter users based on search query
  const filterUsers = (users, query) => {
    if (!query) return users;
    return users.filter(user => 
      user.first_name?.toLowerCase().includes(query.toLowerCase()) ||
      user.last_name?.toLowerCase().includes(query.toLowerCase()) ||
      user.full_name?.toLowerCase().includes(query.toLowerCase()) ||
      user.email?.toLowerCase().includes(query.toLowerCase()) ||
      user.employee_id?.toLowerCase().includes(query.toLowerCase()) ||
      user.badge_id?.toLowerCase().includes(query.toLowerCase())
    );
  };

  // Get user display name
  const getUserDisplayName = () => t('title');

  // Memoize filtered users to prevent unnecessary recalculations
  const filteredUsers = useMemo(() => {
    return filterUsers(users, searchQuery);
  }, [users, searchQuery]);

  if (loading) {
    return (
      <div style={{ padding: '24px' }}>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>{t('loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      {/* Search Bar */}
      <div className="content-card" style={{ marginBottom: '24px' }}>
        <div className="card-content">
          <div className="form-field">
            <label>{t('searchUsers')}</label>
            <input
              type="text"
              placeholder={t('searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
          </div>

      {/* User List */}
      <div className="content-card" style={{ marginBottom: '24px' }}>
        <div className="card-header">
          <div className="card-title">
            {getUserDisplayName()} ({filteredUsers.length})
                </div>
          <button onClick={() => setShowUserForm(true)} className="btn btn-primary">
            {t('addUser')}
          </button>
                </div>
        <div className="card-content">
          <div className="user-list-container">
            <table className="user-table">
              <thead>
                <tr>
                  <th>{t('name')}</th>
                  <th>{t('email')}</th>
                  <th>{t('employeeId')}</th>
                  <th>{t('badgeId')}</th>
                  <th>{t('role')}</th>
                  <th>{t('department')}</th>
                  <th>{t('status')}</th>
                  <th>{t('actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map(user => (
                  <tr 
                    key={user.id} 
                    className={`user-row ${selectedUser?.id === user.id ? 'selected' : ''}`}
                    onClick={() => startEditingUser(user)}
                  >
                    <td className="user-name-cell">
                      <div className="user-name">{user.full_name || `${user.first_name} ${user.last_name}`}</div>
                    </td>
                    <td className="user-email-cell">
                      <span className="user-email">{user.email || t('noEmail')}</span>
                    </td>
                    <td className="user-employee-id-cell">
                      <span className="user-employee-id">{user.employee_id || t('noId')}</span>
                    </td>
                    <td className="user-badge-id-cell">
                      <span className="user-badge-id">{user.badge_id || t('noBadge')}</span>
                    </td>
                    <td className="user-role-cell">
                      <span className="user-role">{getRoleName(user.role_id)}</span>
                    </td>
                    <td className="user-department-cell">
                      <span className="user-department">{getDepartmentName(user.primary_department_id)}</span>
                    </td>
                    <td className="user-status-cell">
                      <span className={`status ${user.status?.toLowerCase()}`}>
                      {user.status === 'active' ? t('statusActive') : user.status === 'inactive' ? t('statusInactive') : user.status === 'on_leave' ? t('statusOnLeave') : (user.status || t('unknown'))}
                    </span>
                    </td>
                    <td className="user-actions-cell">
                      <div className="action-buttons">
                        <button 
                          className="btn btn-edit btn-icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            startEditingUser(user);
                          }}
                          title={t('editUser')}
                        >
                          <Edit size={16} />
                        </button>
                        <button 
                          className="btn btn-delete btn-icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            setUserToDelete(user);
                          }}
                          title={t('deleteUser')}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredUsers.length === 0 && (
              <div className="empty-state">
                <p>{t('noUsersFound')}</p>
                <button onClick={() => setShowUserForm(true)} className="btn btn-primary">
                  {t('addFirstUser')}
                </button>
              </div>
            )}
          </div>
        </div>
            </div>


      {/* Add/Edit User Form Modal */}
      <Modal
        isOpen={showUserForm}
        onClose={cancelEditing}
        title={selectedUser ? t('editUserTitle') : t('addNewUserTitle')}
        size="large"
      >
        <div className="user-form-modal">
          <div className="form-grid">
                <div className="form-field">
                  <label>
                    {t('badgeIdLabel')}
                    <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    value={selectedUser ? (editingUser?.badge_id || '') : userForm.badge_id}
                    onChange={(e) => {
                      if (selectedUser) {
                        setEditingUser(prev => ({ ...prev, badge_id: e.target.value }));
                      } else {
                        setUserForm(prev => ({ ...prev, badge_id: e.target.value }));
                      }
                    }}
                  />
              </div>

                <div className="form-field">
                  <label>
                    {t('employeeIdLabel')}
                    <span className="required">*</span>
                  </label>
                    <input
                    type="text"
                    value={selectedUser ? (editingUser?.employee_id || '') : userForm.employee_id}
                    onChange={(e) => {
                      if (selectedUser) {
                        setEditingUser(prev => ({ ...prev, employee_id: e.target.value }));
                      } else {
                        setUserForm(prev => ({ ...prev, employee_id: e.target.value }));
                      }
                    }}
                  />
                </div>
                
                <div className="form-field">
                  <label>
                    {t('firstName')}
                    <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    value={userForm.first_name}
                    onChange={(e) => setUserForm(prev => ({ ...prev, first_name: e.target.value }))}
                  />
                  </div>

                <div className="form-field">
                  <label>
                    {t('lastName')}
                    <span className="required">*</span>
                  </label>
                    <input
                      type="text"
                    value={userForm.last_name}
                    onChange={(e) => setUserForm(prev => ({ ...prev, last_name: e.target.value }))}
                  />
              </div>

                <div className="form-field">
                  <label>
                    {t('emailLabel')}
                    <span className="required">*</span>
                  </label>
                  <input
                    type="email"
                    value={userForm.email}
                    onChange={(e) => setUserForm(prev => ({ ...prev, email: e.target.value }))}
                    placeholder={t('emailPlaceholder')}
                  />
              </div>

                <div className="form-field">
                  <label>
                    {t('password')}
                    <span className="required">*</span>
                  </label>
                  <input
                    type="password"
                    value={userForm.password}
                    onChange={(e) => setUserForm(prev => ({ ...prev, password: e.target.value }))}
                    placeholder={t('passwordPlaceholder')}
                  />
              </div>

                <div className="form-field">
                  <label>
                    {t('confirmPassword')}
                    <span className="required">*</span>
                  </label>
                  <input
                    type="password"
                    value={userForm.confirmPassword}
                    onChange={(e) => setUserForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                    placeholder={t('confirmPasswordPlaceholder')}
                  />
              </div>

                <div className="form-field">
                  <label>
                    {t('primaryPhone')}
                    <span className="required">*</span>
                  </label>
                  <input
                    type="tel"
                    value={userForm.phone_primary}
                    onChange={(e) => setUserForm(prev => ({ ...prev, phone_primary: e.target.value }))}
                  />
              </div>

                <div className="form-field">
                  <label>
                    {t('roleLabel')}
                    <span className="required">*</span>
                  </label>
                    <select
                    value={userForm.role_id}
                    onChange={(e) => setUserForm(prev => ({ ...prev, role_id: e.target.value }))}
                  >
                    <option value="">{t('selectRole')}</option>
                    {roles.map(role => (
                      <option key={role.id} value={role.id}>{role.name}</option>
                      ))}
                    </select>
              </div>

                <div className="form-field">
                  <label>
                    {t('primaryDepartment')}
                    <span className="required">*</span>
                  </label>
                        <select
                    value={userForm.primary_department_id}
                    onChange={(e) => setUserForm(prev => ({ ...prev, primary_department_id: e.target.value }))}
                        >
                          <option value="">{t('selectDepartment')}</option>
                          {departments.map(dept => (
                            <option key={dept.id} value={dept.id}>{dept.name}</option>
                          ))}
                        </select>
                      </div>

                <div className="form-field">
                  <label>{t('specialty')}</label>
                        <input
                    type="text"
                    value={userForm.specialty}
                    onChange={(e) => setUserForm(prev => ({ ...prev, specialty: e.target.value }))}
                        />
                      </div>

                <div className="form-field">
                  <label>
                    {t('status')}
                    <span className="required">*</span>
                  </label>
                    <select
                    value={userForm.status}
                    onChange={(e) => setUserForm(prev => ({ ...prev, status: e.target.value }))}
                    >
                      <option value="active">{t('statusActive')}</option>
                      <option value="inactive">{t('statusInactive')}</option>
                      <option value="on_leave">{t('statusOnLeave')}</option>
                    </select>
                </div>
              </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={cancelEditing}
            >
              {t('cancel')}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={selectedUser ? saveUserChanges : addUser}
              disabled={
                !userForm.first_name || !userForm.last_name || !userForm.email
              }
            >
              {selectedUser ? t('saveChanges') : t('addUserButton')}
            </button>
          </div>
        </div>
      </Modal>

      {userToDelete && (
        <Modal
          isOpen={!!userToDelete}
          onClose={() => setUserToDelete(null)}
          title={t('deleteUser')}
        >
          <p>{t('confirmDeleteUser')}</p>
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '1rem' }}>
            <button type="button" className="btn btn-secondary" onClick={() => setUserToDelete(null)}>
              {t('common:cancel')}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                deleteUser(userToDelete.id);
                setUserToDelete(null);
              }}
            >
              {t('deleteUser')}
            </button>
          </div>
        </Modal>
      )}

      {error && (
        <div className="error-message">
          ❌ {error}
        </div>
      )}
    </div>
  );
};

export default UserManagement;